import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { format } from "date-fns";
import React, { useMemo } from "react";

export interface PrintingFilters {
  status?: string;
  vendor?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  search_data?: string;
  sort_field?: string;
  sort_direction?: "asc" | "desc";
}

interface PrintingFilterPanelProps {
  currentFilters: PrintingFilters;
  onApplyFilters: (filters: Partial<PrintingFilters>) => void;
  onResetFilters: () => void;
  vendors?: Array<{ id: string; name: string }>; // Made optional with default
}

const statusOptions = [
  { value: "ALL_STATUS", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "printing", label: "Printing In Progress" },
  { value: "completed", label: "Completed" },
];

export const PrintingFilterPanel: React.FC<PrintingFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  vendors = [], // Default to empty array
}) => {
  // Memoize vendor options to prevent unnecessary re-renders
  const vendorOptions = useMemo(() => {
    const baseOptions = [{ value: "ALL_VENDORS", label: "All Vendors" }];
    const vendorOptionsList = vendors.map((vendor) => ({
      value: vendor.id,
      label: vendor.name,
    }));
    return [...baseOptions, ...vendorOptionsList];
  }, [vendors]);

  // Define form fields using the dynamic form structure
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "Select status",
        options: statusOptions,
        defaultValue: "ALL_STATUS",
        containerClassName: "space-y-2",
        className: "w-full",
      },
      {
        name: "vendor",
        label: "Vendor",
        type: "select",
        placeholder: "Select vendor",
        options: vendorOptions,
        defaultValue: "ALL_VENDORS",
        containerClassName: "space-y-2",
        className: "w-full",
      },
      {
        name: "startDate",
        label: "Start Date",
        type: "datepicker",
        placeholder: "Pick start date",
        containerClassName: "space-y-2",
        className: "w-full",
      },
      {
        name: "endDate",
        label: "End Date",
        type: "datepicker",
        placeholder: "Pick end date",
        containerClassName: "space-y-2",
        className: "w-full",
      },
    ],
    [vendorOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  // Handle form submission
  const handleApplyFilters = (formData: any) => {
    const filtersToApply: Partial<PrintingFilters> = {};

    // Only include non-default values
    if (formData.status && formData.status !== "ALL_STATUS") {
      filtersToApply.status = formData.status;
    }
    if (formData.vendor && formData.vendor !== "ALL_VENDORS") {
      filtersToApply.vendor = formData.vendor;
    }
    if (formData.startDate) {
      // Ensure proper date format
      const startDate = new Date(formData.startDate);
      filtersToApply.startDate = format(startDate, "yyyy-MM-dd");
    }
    if (formData.endDate) {
      // Ensure proper date format
      const endDate = new Date(formData.endDate);
      filtersToApply.endDate = format(endDate, "yyyy-MM-dd");
    }

    onApplyFilters(filtersToApply);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    // Reset form to default values
    form.reset({
      status: "ALL_STATUS",
      vendor: "ALL_VENDORS",
      startDate: "",
      endDate: "",
    });

    // Call the parent reset handler
    onResetFilters();
  };

  // Custom footer with Apply and Reset buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 lg:col-span-4 flex justify-end gap-2 border-gray-200">
      <Button
        type="button"
        variant="outline"
        onClick={handleResetFilters}
        className="px-4 py-2"
      >
        Reset Filters
      </Button>
      <Button type="submit" className="px-6 py-2">
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={handleApplyFilters}
        onReset={handleResetFilters}
        config={{
          layout: "horizontal",
          showSubmitButton: false,
          showResetButton: false,
          classes: {
            form: "w-full",
            fieldContainer: "flex flex-col space-y-1",
            label: "block text-sm font-medium text-gray-700 mb-1",
            input:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
            select:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white",
            error: "text-red-500 text-sm mt-1",
            helperText: "text-gray-500 text-sm mt-1",
          },
        }}
        gridColumns={4}
        responsiveColumns={{
          sm: 1,
          md: 2,
          lg: 4,
        }}
        renderCustomFooter={renderCustomFooter}
        className="space-y-6"
      />
    </div>
  );
};

export default PrintingFilterPanel;
